import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api from "@/lib/api";

export default function SupervisorsAdmin({ reference, onReload }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => { const { data } = await api.get("/supervisors"); setList(data); onReload?.(); };
  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  const newOne = () => { setEditing({ name:"", email:"", departments:[], is_admin:false, password:"changeme123" }); setOpen(true); };
  const editOne = (s) => { setEditing({ ...s, password:"" }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...editing };
      if (!payload.password) delete payload.password;
      if (editing.id) {
        await api.patch(`/supervisors/${editing.id}`, payload);
        toast.success("Supervisor updated");
      } else {
        await api.post("/supervisors", payload);
        toast.success("Supervisor created");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this supervisor?")) return;
    try { await api.delete(`/supervisors/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleDept = (name, checked) => {
    setEditing((s) => ({ ...s, departments: checked ? [...s.departments, name] : s.departments.filter((d) => d !== name) }));
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-heading text-lg font-semibold">Supervisors</h3>
        <Button data-testid="sup-new-btn" onClick={newOne} className="gap-1.5"><Plus className="w-4 h-4"/> New supervisor</Button>
      </div>
      <div className="border border-border rounded-lg overflow-hidden bg-[#1E1E1E]">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Departments</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {list.map((s) => (
              <TableRow key={s.id} data-testid={`sup-row-${s.id}`}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell className="text-muted-foreground">{s.email}</TableCell>
                <TableCell>{s.is_admin ? <span className="inline-flex items-center gap-1 text-primary font-medium"><ShieldCheck className="w-4 h-4"/> Admin</span> : "Supervisor"}</TableCell>
                <TableCell className="text-muted-foreground text-xs">{s.departments.join(", ") || "—"}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button data-testid={`sup-edit-${s.id}`} size="icon" variant="ghost" onClick={() => editOne(s)}><Pencil className="w-4 h-4"/></Button>
                  <Button data-testid={`sup-del-${s.id}`} size="icon" variant="ghost" onClick={() => remove(s.id)}><Trash2 className="w-4 h-4"/></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="sup-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit supervisor" : "New supervisor"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <F label="Name"><Input data-testid="sup-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}/></F>
              <F label="Email"><Input data-testid="sup-email" type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })}/></F>
              <F label={editing.id ? "New password (leave blank to keep current)" : "Password"}>
                <Input data-testid="sup-password" type="password" value={editing.password || ""} onChange={(e) => setEditing({ ...editing, password: e.target.value })}/>
              </F>
              <div className="flex items-center gap-2">
                <Checkbox data-testid="sup-is-admin" checked={editing.is_admin} onCheckedChange={(c) => setEditing({ ...editing, is_admin: !!c })}/>
                <Label className="text-sm">Main manager (admin)</Label>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground font-semibold">Departments</Label>
                <div className="grid grid-cols-2 gap-1 mt-2 max-h-48 overflow-y-auto border border-border rounded-md p-3">
                  {reference?.departments.map((d) => (
                    <label key={d.id} className="flex items-center gap-2 text-sm">
                      <Checkbox data-testid={`sup-dept-${d.name}`} checked={editing.departments.includes(d.name)} onCheckedChange={(c) => toggleDept(d.name, !!c)}/> {d.name}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="sup-save-btn" onClick={save}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground font-semibold">{label}</Label>
      {children}
    </div>
  );
}
